/**
 * Currency and amount formatting.
 *
 * Storage convention: integer paisa (1 rupee = 100 paisa).
 * Display convention for UAT: US-style digit grouping only — comma after every
 * three digits (`123,456`, `1,234,567`). Do not use South Asian lakh/crore
 * grouping (`1,23,456`).
 *
 * Use:
 *   formatAmountUS(123456)                         // "123,456"
 *   formatCurrencyAmount(125000000)                // "1,250,000"
 *   formatCurrency(125000000)                      // "Rs. 1,250,000"
 *   formatCurrency(125000000, { prefix: false })   // "1,250,000"
 */

const RS_PREFIX = 'Rs.';

/**
 * Format a raw numeric amount using US-style comma grouping.
 *
 * This is for already-denominated values, for example rupees/dollars as whole
 * units in UI summaries or CSV display output. It intentionally does not add a
 * currency prefix.
 */
export function formatAmountUS(
  value: number,
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
): string {
  if (!Number.isFinite(value)) return '—';

  const { minimumFractionDigits = 0, maximumFractionDigits = 0 } = options;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
}

/**
 * Apply US-style digit grouping to an integer:
 *   1234567  ->  "1,234,567"
 *   123456   ->  "123,456"
 *   123      ->  "123"
 */
export function formatUsGrouped(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const rounded = Math.trunc(n);
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(rounded);
}

/**
 * Backwards-compatible export for older imports.
 *
 * Despite the old name, this now follows the UAT requirement: US-style grouping
 * only. New code should use `formatUsGrouped` or `formatAmountUS` directly.
 */
export function formatIndianGrouped(n: number): string {
  return formatUsGrouped(n);
}

/**
 * Format an integer paisa amount as a whole-unit string with US-style grouping.
 * Whole-paisa amounts are displayed without `.00` because the UAT requirement
 * is `123,456`. Fractional rupees are still preserved when present.
 */
export function formatCurrencyAmount(paisa: number): string {
  if (!Number.isFinite(paisa)) return '—';

  const isNegative = paisa < 0;
  const abs = Math.abs(Math.round(paisa));
  const rupees = Math.floor(abs / 100);
  const fractionalPaisa = abs % 100;

  const whole = formatUsGrouped(rupees);
  const fraction = fractionalPaisa === 0 ? '' : `.${fractionalPaisa.toString().padStart(2, '0')}`;

  return `${isNegative ? '-' : ''}${whole}${fraction}`;
}

/**
 * Format with the `Rs.` prefix unless `prefix: false` is supplied.
 */
export function formatCurrency(
  paisa: number,
  options: { compact?: boolean; prefix?: boolean } = {},
): string {
  const { compact = false, prefix = true } = options;
  const formatted = compact ? formatCurrencyCompact(paisa) : formatCurrencyAmount(paisa);
  return prefix ? `${RS_PREFIX} ${formatted}` : formatted;
}

/**
 * Compact form for stat tiles and tight UIs.
 *
 * Uses international suffixes instead of lakh/crore suffixes so it does not
 * reintroduce South Asian number grouping concepts.
 */
export function formatCurrencyCompact(paisa: number): string {
  if (!Number.isFinite(paisa)) return '—';

  const isNegative = paisa < 0;
  const abs = Math.abs(paisa);
  const rupees = abs / 100;

  let value: number;
  let suffix: string;

  if (rupees >= 1_000_000_000) {
    value = rupees / 1_000_000_000;
    suffix = 'B';
  } else if (rupees >= 1_000_000) {
    value = rupees / 1_000_000;
    suffix = 'M';
  } else if (rupees >= 1_000) {
    value = rupees / 1_000;
    suffix = 'K';
  } else {
    return `${isNegative ? '-' : ''}${formatAmountUS(rupees, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  }

  const valueString = value % 1 === 0 ? value.toFixed(0) : value.toFixed(1).replace(/\.0$/, '');
  return `${isNegative ? '-' : ''}${valueString} ${suffix}`;
}
